import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Bell, Check, AlertTriangle, AlertCircle } from "lucide-react";
import SiteSelector, { useSiteSelector } from "../components/SiteSelector";
import { toast } from "sonner";

const SEVS = { critical: { color: "#FF3B30", icon: AlertCircle }, warning: { color: "#FFB300", icon: AlertTriangle } };

export default function Alarms() {
  const { sites, siteId, setSiteId } = useSiteSelector();
  const [alarms, setAlarms] = useState([]);
  const [filter, setFilter] = useState("active");

  const load = async () => {
    if (!siteId) return;
    try {
      const { data } = await api.get("/alarms", { params: { active_only: filter === "active", site_id: siteId } });
      setAlarms(data);
    } catch {}
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, [filter, siteId]);

  const ack = async (id) => {
    try {
      await api.post(`/alarms/${id}/ack`, { note: "Acknowledged via console" });
      toast.success("Alarm acknowledged");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="scada-label">ALARM MANAGEMENT</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter">Alarms</h1>
        </div>
        <div className="flex gap-2 items-center" data-testid="alarm-filters">
          <SiteSelector sites={sites} siteId={siteId} setSiteId={setSiteId} />
          {["active", "all"].map((f) => (
            <button
              key={f}
              data-testid={`filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 font-mono-metric uppercase tracking-wider text-xs border ${filter === f ? "border-[#00E5FF] text-[#00E5FF]" : "border-[#2C3238] text-[#9BA4B5] hover:text-[#E3E8EE]"}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto w-full scada-panel" data-testid="alarm-list">
        <div className="min-w-[700px]">
          <div className="grid grid-cols-12 px-4 py-3 border-b border-[#2C3238] scada-label">
          <div className="col-span-1">SEV</div>
          <div className="col-span-4">TAG</div>
          <div className="col-span-2">VALUE</div>
          <div className="col-span-3">TIMESTAMP</div>
          <div className="col-span-2 text-right">STATE</div>
        </div>
        {alarms.length === 0 && (
          <div className="px-4 py-10 text-center text-[#5A6573] font-mono-metric text-sm">
            <Bell className="inline mr-2" size={14} /> No alarms to display
          </div>
        )}
        {alarms.map((a) => {
          const s = SEVS[a.severity] || SEVS.warning;
          const Icon = s.icon;
          const isCrit = a.severity === "critical";
          return (
            <div
              key={a.id}
              className={`grid grid-cols-12 px-4 py-3 border-b border-[#2C3238]/60 items-center text-sm border-l-4 hover:bg-[#1E2226]/40 transition-colors ${isCrit ? "alarm-critical-pulse" : ""}`}
              style={{ borderLeftColor: s.color }}
              data-testid={`alarm-row-${a.id}`}
            >
              <div className="col-span-1 flex items-center gap-2">
                <Icon size={13} style={{ color: s.color }} className={isCrit ? "animate-pulse" : ""} />
                <span className="font-mono-metric uppercase text-[9px] font-bold" style={{ color: s.color, textShadow: `0 0 8px ${s.color}50` }}>{a.severity}</span>
              </div>
              <div className="col-span-4 text-[#E3E8EE] font-semibold">{a.tag_name}</div>
              <div className="col-span-2 font-mono-metric text-[#E3E8EE] font-medium">{a.value} <span className="text-[10px] text-[#9BA4B5]">{a.unit}</span></div>
              <div className="col-span-3 font-mono-metric text-[#9BA4B5] text-xs">{new Date(a.ts).toLocaleString()}</div>
              <div className="col-span-2 flex justify-end items-center gap-2">
                {a.active && <span className="font-mono-metric text-[9px] text-[#FF3B30] font-bold uppercase px-1.5 py-0.5 bg-[#FF3B30]/10 border border-[#FF3B30]/20 text-glow-critical">ACTIVE</span>}
                {!a.active && <span className="font-mono-metric text-[9px] text-[#78909C] font-medium uppercase px-1.5 py-0.5 bg-[#78909C]/10 border border-[#78909C]/20">CLEARED</span>}
                {!a.acknowledged && (
                  <button
                    onClick={() => ack(a.id)}
                    data-testid={`ack-${a.id}`}
                    className="border border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/15 px-2.5 py-1 text-[9px] font-mono-metric uppercase flex items-center gap-1 shadow-[0_0_8px_rgba(0,229,255,0.1)]"
                  >
                    <Check size={9} /> ACK
                  </button>
                )}
                {a.acknowledged && <span className="font-mono-metric text-[9px] text-[#00E676] font-bold uppercase px-1.5 py-0.5 bg-[#00E676]/10 border border-[#00E676]/20 text-glow-normal">ACK</span>}
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}
